import {
  PublicClientApplication,
  LogLevel,
  type Configuration,
  type RedirectRequest,
  type SilentRequest,
} from '@azure/msal-browser'
import { environment } from '@/app/core/config/environment'

/**
 * Whether real Azure AD B2C is wired. In mock mode (VITE_MOCK_AUTH=true) or when
 * B2C config is absent we skip MSAL entirely and AuthProvider serves a local user.
 */
export const isB2CEnabled = !environment.mockAuth && Boolean(environment.b2c.clientId)

const msalConfig: Configuration = {
  auth: {
    clientId: environment.b2c.clientId,
    authority: environment.b2c.authority,
    knownAuthorities: environment.b2c.knownAuthority ? [environment.b2c.knownAuthority] : [],
    redirectUri: environment.b2c.redirectUri,
    postLogoutRedirectUri: environment.b2c.postLogoutRedirectUri,
  },
  cache: {
    cacheLocation: 'localStorage',
  },
  system: {
    loggerOptions: {
      logLevel: environment.devMode ? LogLevel.Warning : LogLevel.Error,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return
        if (level === LogLevel.Error) console.error('[MSAL]', message)
      },
    },
  },
}

/**
 * Singleton MSAL instance. Imported directly by the Apollo auth link so token
 * acquisition does not depend on React context/provider ordering. Null when B2C
 * is disabled (mock/dev) so callers can no-op.
 */
export const msalInstance: PublicClientApplication | null = isB2CEnabled
  ? new PublicClientApplication(msalConfig)
  : null

/** Scopes requested at login — include the API scope so consent covers GraphQL calls. */
export const loginRequest: RedirectRequest = {
  scopes: environment.b2c.apiScope
    ? ['openid', 'profile', 'offline_access', environment.b2c.apiScope]
    : ['openid', 'profile', 'offline_access'],
}

/** Silent/redirect request used to obtain the API access token sent as Bearer. */
export const apiTokenRequest: Pick<SilentRequest, 'scopes'> = {
  scopes: environment.b2c.apiScope ? [environment.b2c.apiScope] : [],
}

/**
 * MSAL v3 requires initialize() to complete before any other API call, and we
 * must process the redirect response on app load. Safe no-op when B2C disabled.
 * Call once in main.tsx BEFORE rendering.
 */
export async function initializeMsal(): Promise<void> {
  if (!msalInstance) return
  await msalInstance.initialize()
  const result = await msalInstance.handleRedirectPromise()
  if (result?.account) {
    msalInstance.setActiveAccount(result.account)
  } else {
    const accounts = msalInstance.getAllAccounts()
    if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
      msalInstance.setActiveAccount(accounts[0])
    }
  }
}
